import React, { useEffect, useState } from 'react';
import {
  CONSENT_VERSION,
  getConsentFromCookie,
  COOKIE_CONSENT_UPDATED_EVENT,
  type CookieConsent,
  setConsentCookie,
} from 'utils/cookieConsent';
import { pushConsentUpdate, setAnalyticsReady } from 'utils/gtagLoader';

const CookieBanner: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const parsed = getConsentFromCookie();

    // No consent stored – show banner
    if (!parsed) {
      setIsOpen(true);
      return;
    }

    // If we ever change the version, we can re-ask for consent
    if (!parsed.version || parsed.version !== CONSENT_VERSION) {
      setIsOpen(true);
      return;
    }

    pushConsentUpdate({
      analytics: parsed.analytics,
      marketing: parsed.marketing,
    });
    setAnalyticsReady(parsed.analytics);

    // We have valid consent stored, so keep the banner hidden
    setIsOpen(false);
  }, []);

  const saveConsent = (opts: { analytics: boolean; marketing: boolean }) => {
    const consent: CookieConsent = {
      necessary: true, // always true – strictly necessary cookies are required for the site to function
      analytics: opts.analytics,
      marketing: opts.marketing,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };

    setConsentCookie(consent);
    pushConsentUpdate({
      analytics: opts.analytics,
      marketing: opts.marketing,
    });
    setAnalyticsReady(opts.analytics);
    setIsOpen(false);
    window.dispatchEvent(new Event(COOKIE_CONSENT_UPDATED_EVENT));
  };

  const handleAcceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
  };

  const handleRejectNonEssential = () => {
    saveConsent({ analytics: false, marketing: false });
  };

  const handleSavePreferences = () => {
    saveConsent({ analytics, marketing });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 bg-slate-900 text-white">
      <div className="container-custom py-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2 max-w-2xl">
          <p className="text-sm font-semibold">We use cookies</p>
          <p className="text-xs text-slate-200">
            We use essential cookies to make our site work. We&apos;d also like to use analytics and marketing
            cookies to help us improve the site and show relevant content. You can change your choices at any
            time in our Cookies Policy.
          </p>
          <a
            href="/cookies-policy"
            className="text-xs underline text-slate-200 hover:text-white"
          >
            Read our Cookie Policy
          </a>
        </div>

        <div className="flex flex-col gap-3 md:items-end">
          <div className="flex flex-col gap-2 text-xs">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked disabled />
              <span>Strictly necessary cookies</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
              />
              <span>Analytics cookies</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
              />
              <span>Marketing cookies</span>
            </label>
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              onClick={handleRejectNonEssential}
              className="px-3 py-1.5 text-xs rounded border border-slate-500 hover:bg-slate-800"
            >
              Reject non-essential
            </button>
            <button
              type="button"
              onClick={handleSavePreferences}
              className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600"
            >
              Save preferences
            </button>
            <button
              type="button"
              onClick={handleAcceptAll}
              className="px-3 py-1.5 text-xs rounded bg-primary-600 hover:bg-primary-700 text-white"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
